<script lang="ts" setup>
const props = withDefaults(
  defineProps<{
    source?: string
    alt?: string
    aspectRatio?: number
    lazy?: boolean
    mode?: 'contain' | 'cover'
  }>(),
  {
    alt: '',
    aspectRatio: 1,
    lazy: true,
    mode: 'contain',
  },
)

const imageContainerRef = ref<HTMLElement>()
const imageShouldLoad = ref(props.source && !props.lazy)

const shouldHandleLazyLoading = computed(
  () => Boolean(props.source) && props.lazy && !imageShouldLoad.value && props.mode === 'contain',
)
const shouldUseNativeLazyLoading = computed(
  () => Boolean(props.source) && props.lazy && props.mode === 'cover',
)

let observer: ReturnType<typeof useIntersectionObserver> | undefined

// If source changes, reset image loading
watch(
  () => props.source,
  () => {
    imageShouldLoad.value = !props.lazy
  },
)

// Handle lazy loading
watch(
  shouldHandleLazyLoading,
  value => {
    if (value) {
      if (!observer) {
        observer = useIntersectionObserver(
          imageContainerRef,
          ([{ isIntersecting }]) => {
            if (isIntersecting) {
              imageShouldLoad.value = true
            }
          },
          {
            rootMargin: '100% 0px 100% 0px',
          },
        )
      } else {
        observer.resume()
      }
    } else {
      observer?.pause()
    }
  },
  { immediate: true },
)
</script>

<template>
  <div
    ref="imageContainerRef"
    class="cropped-image"
    :data-mode="mode"
    :style="{
      '--aspect-ratio': `${props.aspectRatio} / 1`,
      '--source':
        imageShouldLoad && source && props.mode === 'contain' ? `url('${source}')` : 'none',
    }"
  >
    <img
      v-if="source"
      :src="imageShouldLoad || shouldUseNativeLazyLoading ? source : undefined"
      :alt="alt"
      :loading="shouldUseNativeLazyLoading ? 'lazy' : undefined"
    />
  </div>
</template>

<style lang="scss" scoped>
.cropped-image {
  aspect-ratio: var(--aspect-ratio);
  background-color: var(--gray-100);
  background-image: var(--source);
  background-size: cover;
  background-position: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: v-bind('mode');
    object-position: center;
    backdrop-filter: blur(20px) brightness(0.5);
  }

  &[data-mode='cover'] img {
    object-position: center 12.5%;
  }
}
</style>
